// Domain models for the Devices section. Mirrors the shape the backend
// `devices` query is expected to return once it exists; the client gates the
// whole section on the MANAGE_DEVICES capability (see lib/capabilities.ts) and
// the API must independently enforce that capability server-side.

export type DeviceStatus = 'ONLINE' | 'OFFLINE' | 'PENDING';
export type CameraStatus = 'ONLINE' | 'OFFLINE' | 'PENDING';

export interface Camera {
  id: string;
  // Human label for the cooking station this camera watches (e.g. "Grill Cam").
  name: string;
  status: CameraStatus;
  // Latest still frame from the camera. `null` when no frame is available yet
  // (newly added, offline, or still processing) — the UI renders an empty state.
  imageUrl: string | null;
}

export interface Device {
  // Short human-facing device id (4 digits in the current hardware).
  id: string;
  // Full hardware serial (20 digits).
  serial: string;
  status: DeviceStatus;
  // Pre-formatted "pinged just now" / "last reachable 2h ago" style label.
  lastPingLabel: string;
  // Location this device is installed at, or `null` for an unpaired device
  // that has been provisioned but not yet associated with a location.
  locationName: string | null;
  // Cameras attached to this device. Always empty for unpaired devices.
  cameras: Camera[];
}

export function isUnpaired(device: Device): boolean {
  return device.locationName === null;
}

// Title shown in the card header: the location name for a paired device, or a
// serial-bearing descriptor for an unpaired one.
export function deviceTitle(device: Device): string {
  if (isUnpaired(device)) {
    return `Device: ${device.id} (serial number: ${device.serial})`;
  }
  return device.locationName as string;
}
